import React, { useMemo, useState } from "react"
import { Formik, FormikProps } from "formik"
import MyDateMonthYearFormField from "@common-components/fields/MyDateMonthYearFormField"
import moment from "moment"

interface Props {
     mois: string | undefined
     onMonthChange: (mois: string | undefined, formatted: string | undefined) => void
}

const Step1MonthSelector = ({ mois, onMonthChange }: Props) => {
     const [isEditing, setIsEditing] = useState(false)

     // Format the YYYY-MM string for a clean display (e.g., "Janvier 2026")
     const displayDate = useMemo(() => {
          if (!mois) return ""
          return moment(mois, "YYYY-MM").format("MMMM YYYY")
     }, [mois])

     // Compact View: Rendered when a month is selected and we aren't actively editing
     if (mois && !isEditing) {
          return (
               <div className="card shadow-none border border-gray-300 rounded-3">
                    <div className="card-body py-4 px-6">
                         <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                   <div className="symbol symbol-40px me-4">
                                        <div className="symbol-label bg-light-success">
                                             <i className="fas fa-calendar-check text-success fs-4"></i>
                                        </div>
                                   </div>
                                   <div>
                                        <span className="text-gray-400 fw-bold fs-8 d-block text-uppercase">1. Période sélectionnée</span>
                                        <span className="text-gray-800 fw-bolder fs-5 text-capitalize">{displayDate}</span>
                                   </div>
                              </div>

                              <button className="btn btn-sm btn-light-primary fw-bolder px-4" onClick={() => setIsEditing(true)}>
                                   <i className="fas fa-pencil-alt fs-7 me-2"></i>
                                   Modifier
                              </button>
                         </div>
                    </div>
               </div>
          )
     }

     // Expanded View: The full selection card
     return (
          <div className="card shadow-none border border-gray-300 rounded-3 mb-8">
               <div className="card-body py-8">
                    <div className="d-flex flex-column align-items-center text-center">
                         <div className="symbol symbol-60px mb-5">
                              <div className="symbol-label bg-light-primary">
                                   <i className="fas fa-calendar-alt fs-2x text-primary"></i>
                              </div>
                         </div>

                         <h3 className="fw-bolder mb-2">1. Période de déclaration</h3>
                         <p className="text-gray-500 fw-semibold fs-6 mb-6 w-100 w-md-400px">Sélectionnez le mois correspondant aux factures que vous souhaitez traiter.</p>

                         <div className={"w-100 w-md-300px position-relative"}>
                              <Formik initialValues={{ date: mois || "" }} enableReinitialize onSubmit={() => {}}>
                                   {(formikProps: FormikProps<{ date: string }>) => (
                                        <MyDateMonthYearFormField
                                             name={"date"}
                                             formikProps={formikProps}
                                             placeholder={"MM/AAAA"}
                                             classNameLayout="m-0"
                                             onChange={(val: string | null) => {
                                                  if (val && val.includes("-")) {
                                                       const [year, month] = val.split("-")
                                                       if (year && month) {
                                                            onMonthChange(val, `${year}-${month}-01`)
                                                            setIsEditing(false) // Auto-collapse on selection
                                                       } else {
                                                            onMonthChange(val, undefined)
                                                       }
                                                  } else {
                                                       onMonthChange(undefined, undefined)
                                                  }
                                             }}
                                        />
                                   )}
                              </Formik>
                         </div>

                         {/* Cancel button if we were editing an existing value */}
                         {mois && (
                              <button className="btn btn-link btn-color-gray-500 btn-active-color-primary mt-4" onClick={() => setIsEditing(false)}>
                                   Annuler
                              </button>
                         )}
                    </div>
               </div>
          </div>
     )
}

export default Step1MonthSelector
